import { z } from 'zod';
import { Schema } from 'read-excel-file/types/parseData/parseDataSchema';

const recordBoolean = z.preprocess((val) => {
  // Support native excel boolean values
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val === 1;

  // Support T, TRUE, 1, F, FALSE, 0 for booleans in our records imports
  if (typeof val === 'string') {
    const normalizedVal = val.toUpperCase() ;
    if (normalizedVal === 'T' || normalizedVal === 'TRUE' || normalizedVal === '1') return true ;
    if (normalizedVal === 'F' || normalizedVal === 'FALSE' || normalizedVal === '0') return false ;
  }

  return val;
}, z.boolean());

const recordGrade = z.preprocess((val) => {
  if (typeof val === 'number') return val;

  if (typeof val === 'string') {
    const normalizedVal = val.toUpperCase() ;
    if (normalizedVal === 'K') return 1;
    if (normalizedVal === 'OTHER') return 14;

    const parsed = parseInt(normalizedVal, 10);
    return isNaN(parsed) ? val : parsed;
  }

  return val;
}, z.number().int().gte(1).lte(14));

const coerceRequiredNumber = z.preprocess((val) => {
  if (val === null || val === undefined || (typeof val === 'string' && val.trim() === '')) {
    return undefined;
  }
  return val;
}, z.coerce.number());

const coerceNumberNullish = z.preprocess((val) => {
  if (val === '' || val === null || val === undefined) return null;
  return val;
}, z.coerce.number().nullish());

const RecordBoolean = (val: any) => {
  // Support native excel boolean values
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val === 1;

  if (typeof val === 'string') {
    const normalizedVal = val.toUpperCase() ;
    if (normalizedVal === 'T' || normalizedVal === 'TRUE' || normalizedVal === '1') return true ;
    if (normalizedVal === 'F' || normalizedVal === 'FALSE' || normalizedVal === '0') return false ;

    throw new Error('invalid boolean string');
  }

  throw new Error('invalid boolean string');
};

const RecordGender = (val: any) => {
  if (typeof val === 'string' &&
    (val.toUpperCase() === 'M' ||
    val.toUpperCase() === 'F' ||
    val.toUpperCase() === 'NULL' ||
    val.toUpperCase() === '')) {
      return val;
  }

  throw new Error('invalid gender string');
};

const RecordGrade = (val: any) => {
  if (typeof val === 'string') {
    const normalizedVal = val.toUpperCase() ;
    if (normalizedVal === 'K') return 1;
    if (normalizedVal === 'OTHER') return 14;

    const parsed = parseInt(normalizedVal, 10);
    return isNaN(parsed) ? val : parsed;
  }

  return val;
};

export const CustomerServiceSchema: Schema = {
  teacherUsername: {
    column: 'Teacher Username',
    type: String,
    required: true,
  },
  firstName: {
    column: 'First Name',
    type: String,
    required: true,
  },
  middleName: {
    column: 'Middle Name',
    type: String,
    required: false,
  },
  lastName: {
    column: 'Last Name',
    type: String,
    required: true,
  },
  username: {
    column: 'Username',
    type: String,
    required: true,
  },
  password: {
    column: 'Password',
    type: String,
    required: true,
  },
  gender: {
    column: 'Gender',
    type: RecordGender,
    required: true,
  },
  ellStatus: {
    column: 'ELL',
    type: RecordBoolean,
    required: true,
  },
  sessionLength: {
    column: 'SessionLength',
    type: Number,
    required: true,
  },
  canBeAssignedToTeam: {
    column: 'CanBeAssignedToTeam',
    type: RecordBoolean,
    required: true,
  },
  taskTimerBarEnabled: {
    column: 'TaskTimerBar',
    type: RecordBoolean,
    required: true,
  },
  grade: {
    column: 'Grade',
    type: RecordGrade,
    required: true,
  },
  subscriptionType: {
    column: 'SubscriptionType',
    type: Number,
    required: true,
  },
  curriculum: {
    column: 'Curriculum',
    type: String,
    required: false,
  },
  schoolClass: {
    column: 'Class',
    type: String,
    required: false,
  },
  extraSupport: {
    column: 'ExtraSupportNeeded',
    type: RecordBoolean,
    required: true,
  },
};

export const SchoolAdminSchema: Schema = {
  firstName: {
    column: 'First Name',
    type: String,
    required: true,
  },
  lastName: {
    column: 'Last Name',
    type: String,
    required: true,
  },
  grade: {
    column: 'Grade',
    type: Number,
    required: true,
  },
  primaryTeacherEmail: {
    column: 'Primary Teacher Email',
    type: String,
    required: true,
  },
  username: {
    column: 'Username',
    type: String,
    required: false,
  },
  password: {
    column: 'Password',
    type: String,
    required: false,
  },
  sessionTime: {
    column: 'Session Time',
    type: Number,
    required: false,
  },
};

export const BatchEnrollmentSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  grade: recordGrade,
});

export const CustomerServiceBatchEnrollmentRecordSchema = BatchEnrollmentSchema.extend({
  teacherUsername: z.string().min(1, 'Teacher username is required'),
  middleName: z.string().nullish(),
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
  gender: z.enum(['M', 'F', 'NULL', '']).nullable(),
  ellStatus: recordBoolean,
  sessionLength: coerceRequiredNumber,
  canBeAssignedToTeam: recordBoolean,
  taskTimerBarEnabled: recordBoolean,
  subscriptionType: coerceRequiredNumber,
  curriculum: z.string().nullish(),
  schoolClass: z.string().nullish(),
  extraSupport: recordBoolean,
});

export const SchoolAdminBatchEnrollmentRecordSchema = BatchEnrollmentSchema.extend({
  primaryTeacherEmail: z.email(),
  username: z.string().nullish(),
  password: z.string().nullish(),
  sessionTime: coerceNumberNullish,
});

export type BatchEnrollmentRecord = z.infer<typeof BatchEnrollmentSchema>;
export type CustomerServiceBatchEnrollmentRecord = z.infer<typeof CustomerServiceBatchEnrollmentRecordSchema>;
export type SchoolAdminBatchEnrollmentRecord = z.infer<typeof SchoolAdminBatchEnrollmentRecordSchema>;

export const CustomerServiceRecordHeader = Object.fromEntries(
  Object.entries(CustomerServiceSchema).map(([key, value]) => [
    (value as any).column,
    key
  ])
) as Record<string, keyof typeof CustomerServiceSchema>;

export const SchoolAdminServiceRecordHeader = Object.fromEntries(
  Object.entries(SchoolAdminSchema).map(([key, value]) => [
    (value as any).column,
    key
  ])
) as Record<string, keyof typeof SchoolAdminSchema>;
